import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, User, LogOut, Settings, Heart, ShoppingBag } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, isAdmin, logout } = useAuth();

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b-4 border-teal-400 shadow-sm">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <img 
              src="https://customer-assets.emergentagent.com/job_dadtime-guides/artifacts/y6hj3s71_Dad%20connect%20logo.png" 
              alt="DadConnect" 
              className="h-14 w-auto transform group-hover:scale-105 transition-transform duration-200"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-2">
            <Link to="/about">
              <Button 
                variant={isActive('/about') ? 'default' : 'ghost'}
                className="text-gray-700 hover:text-teal-600 hover:bg-teal-50 transition-colors font-medium text-base px-4 py-2"
              >
                About
              </Button>
            </Link>
            <Link to="/activities">
              <Button 
                variant={isActive('/activities') ? 'default' : 'ghost'}
                className="text-gray-700 hover:text-teal-600 hover:bg-teal-50 transition-colors font-medium text-base px-4 py-2"
              >
                Activities
              </Button>
            </Link>
            <Link to="/resources">
              <Button 
                variant={isActive('/resources') ? 'default' : 'ghost'}
                className="text-gray-700 hover:text-teal-600 hover:bg-teal-50 transition-colors font-medium text-base px-4 py-2"
              >
                Resources
              </Button>
            </Link>
            <Link to="/self-improvement">
              <Button 
                variant={isActive('/self-improvement') ? 'default' : 'ghost'}
                className="text-gray-700 hover:text-teal-600 hover:bg-teal-50 transition-colors font-medium text-base px-4 py-2"
              >
                Self-Improvement
              </Button>
            </Link>
            <Link to="/coaching">
              <Button 
                variant={isActive('/coaching') ? 'default' : 'ghost'}
                className="text-gray-700 hover:text-teal-600 hover:bg-teal-50 transition-colors font-medium text-base px-4 py-2"
              >
                Coaching
              </Button>
            </Link>
            <Link to="/book">
              <Button 
                variant={isActive('/book') ? 'default' : 'ghost'}
                className="text-gray-700 hover:text-teal-600 hover:bg-teal-50 transition-colors font-medium text-base px-4 py-2"
              >
                Book
              </Button>
            </Link>
            
            {isAuthenticated ? (
              <div className="relative ml-4">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-teal-50 transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-teal-500 flex items-center justify-center text-white font-bold text-lg">
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <span className="text-gray-700 font-medium text-base">{user?.name}</span>
                </button>
                
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                      <p className="text-xs text-gray-500">{user?.email}</p>
                    </div>
                    {isAdmin && (
                      <Link to="/admin" onClick={() => setShowUserMenu(false)}>
                        <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-teal-50 flex items-center gap-2">
                          <Settings className="h-4 w-4" />
                          Admin Dashboard
                        </button>
                      </Link>
                    )}
                    <Link to="/profile" onClick={() => setShowUserMenu(false)}>
                      <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-teal-50 flex items-center gap-2">
                        <User className="h-4 w-4" />
                        My Profile
                      </button>
                    </Link>
                    <Link to="/favorites" onClick={() => setShowUserMenu(false)}>
                      <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-teal-50 flex items-center gap-2">
                        <Heart className="h-4 w-4" />
                        My Favorites
                      </button>
                    </Link>
                    <Link to="/orders" onClick={() => setShowUserMenu(false)}>
                      <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-teal-50 flex items-center gap-2">
                        <ShoppingBag className="h-4 w-4" />
                        My Orders
                      </button>
                    </Link>
                    <button 
                      onClick={handleLogout}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <LogOut className="h-4 w-4" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="outline" className="ml-3 border-2 border-teal-400 text-teal-700 hover:bg-teal-50 text-base px-5 py-2">
                    Login
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button className="ml-2 bg-gradient-to-r from-blue-500 via-teal-500 to-green-500 hover:from-blue-600 hover:via-teal-600 hover:to-green-600 text-white shadow-lg text-base px-5 py-2">
                    Sign Up
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-teal-50 transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 space-y-2 border-t border-teal-200">
            <Link to="/about" onClick={() => setIsMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start hover:bg-teal-50">
                About
              </Button>
            </Link>
            <Link to="/activities" onClick={() => setIsMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start hover:bg-teal-50">
                Activities
              </Button>
            </Link>
            <Link to="/resources" onClick={() => setIsMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start hover:bg-teal-50">
                Resources
              </Button>
            </Link>
            <Link to="/self-improvement" onClick={() => setIsMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start hover:bg-teal-50">
                Self-Improvement
              </Button>
            </Link>
            <Link to="/coaching" onClick={() => setIsMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start hover:bg-teal-50">
                Coaching
              </Button>
            </Link>
            <Link to="/book" onClick={() => setIsMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start hover:bg-teal-50">
                Book
              </Button>
            </Link>
            
            {isAuthenticated ? (
              <div className="pt-2 space-y-2 border-t border-teal-200 mt-2">
                <div className="px-4 py-2">
                  <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                </div>
                {isAdmin && (
                  <Link to="/admin" onClick={() => setIsMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start hover:bg-teal-50">
                      <Settings className="h-4 w-4 mr-2" />
                      Admin Dashboard
                    </Button>
                  </Link>
                )}
                <Link to="/profile" onClick={() => setIsMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start hover:bg-teal-50">
                    <User className="h-4 w-4 mr-2" />
                    My Profile
                  </Button>
                </Link>
                <Link to="/favorites" onClick={() => setIsMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start hover:bg-teal-50">
                    <Heart className="h-4 w-4 mr-2" />
                    My Favorites
                  </Button>
                </Link>
                <Link to="/orders" onClick={() => setIsMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start hover:bg-teal-50">
                    <ShoppingBag className="h-4 w-4 mr-2" />
                    My Orders
                  </Button>
                </Link>
                <Button 
                  variant="ghost" 
                  className="w-full justify-start text-red-600 hover:bg-red-50"
                  onClick={() => { handleLogout(); setIsMenuOpen(false); }}
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              </div>
            ) : (
              <div className="pt-2 space-y-2">
                <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                  <Button variant="outline" className="w-full border-2 border-teal-400">
                    Login
                  </Button>
                </Link>
                <Link to="/signup" onClick={() => setIsMenuOpen(false)}>
                  <Button className="w-full bg-gradient-to-r from-blue-500 to-teal-500 text-white">
                    Sign Up
                  </Button>
                </Link>
              </div>
            )}
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;